/**
 * Calendar Tab
 *
 * P&L Calendar visualization showing daily performance.
 * Adapted from Portfolio's PnlCalendar for Trading Analysis.
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import { cn, formatCurrency } from "@/lib/utils";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  MousePointerClickIcon,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTradingAnalysis } from "../trading-analysis-context";
import { AnalysisCard } from "./shared/analysis-card";
import { StatGrid } from "./shared/stat-grid";
import { StatCard } from "./shared/stat-card";
import { useI18n } from "@/features/i18n";
import type { CompletedTrade } from "@nordex/core";

// Site's green and red colors
const SITE_GREEN = "#2ebd85";
const SITE_RED = "#f6465d";

// Inject keyframes for calendar glow effects
const injectCalendarStyles = () => {
  const styleId = "ta-calendar-glow-animation";
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    @keyframes ta-calendar-cell-glow {
      0%, 100% { box-shadow: 0 0 8px var(--glow-color, transparent); }
      50% { box-shadow: 0 0 16px var(--glow-color, transparent); }
    }
  `;
  document.head.appendChild(style);
};

type DayData = {
  date: Date;
  dateStr: string;
  dayNumber: number;
  pnl: number | null;
  grossPnl: number;
  fees: number;
  isCurrentMonth: boolean;
  trades: CompletedTrade[];
};

export function CalendarTab() {
  const { filteredTrades } = useTradingAnalysis();
  const { formatTradingTime, formatDate } = useI18n();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [includeFees, setIncludeFees] = useState(true);
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<DayData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Inject styles on mount
  useEffect(() => {
    injectCalendarStyles();
  }, []);

  // Group trades by day
  const tradesByDate = useMemo(() => {
    const map = new Map<string, CompletedTrade[]>();
    filteredTrades.forEach((trade) => {
      const dateStr = trade.exitTime.toISOString().split("T")[0];
      const existing = map.get(dateStr) || [];
      existing.push(trade);
      map.set(dateStr, existing);
    });
    return map;
  }, [filteredTrades]);

  // Get calendar data for current month
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days: DayData[] = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      const prevDate = new Date(year, month, -startDayOfWeek + i + 1);
      const dateStr = prevDate.toISOString().split("T")[0];
      const dayTrades = tradesByDate.get(dateStr) || [];
      const grossPnl = dayTrades.reduce((sum, t) => sum + t.totalPnl, 0);
      const fees = dayTrades.reduce((sum, t) => sum + t.fees, 0);
      const netPnl = grossPnl - fees;

      days.push({
        date: prevDate,
        dateStr,
        dayNumber: prevDate.getDate(),
        pnl: dayTrades.length > 0 ? (includeFees ? netPnl : grossPnl) : null,
        grossPnl,
        fees,
        isCurrentMonth: false,
        trades: dayTrades,
      });
    }

    // Add days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split("T")[0];
      const dayTrades = tradesByDate.get(dateStr) || [];
      const grossPnl = dayTrades.reduce((sum, t) => sum + t.totalPnl, 0);
      const fees = dayTrades.reduce((sum, t) => sum + t.fees, 0);
      const netPnl = grossPnl - fees;

      days.push({
        date,
        dateStr,
        dayNumber: day,
        pnl: dayTrades.length > 0 ? (includeFees ? netPnl : grossPnl) : null,
        grossPnl,
        fees,
        isCurrentMonth: true,
        trades: dayTrades,
      });
    }

    // Add days after the end of the month to complete the grid
    const totalCells = Math.ceil(days.length / 7) * 7;
    const remainingCells = totalCells - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      const nextDate = new Date(year, month + 1, i);
      const dateStr = nextDate.toISOString().split("T")[0];
      const dayTrades = tradesByDate.get(dateStr) || [];
      const grossPnl = dayTrades.reduce((sum, t) => sum + t.totalPnl, 0);
      const fees = dayTrades.reduce((sum, t) => sum + t.fees, 0);
      const netPnl = grossPnl - fees;

      days.push({
        date: nextDate,
        dateStr,
        dayNumber: i,
        pnl: dayTrades.length > 0 ? (includeFees ? netPnl : grossPnl) : null,
        grossPnl,
        fees,
        isCurrentMonth: false,
        trades: dayTrades,
      });
    }

    return days;
  }, [currentDate, tradesByDate, includeFees]);

  // Calculate summary stats for current month
  const monthSummary = useMemo(() => {
    let winningDays = 0;
    let losingDays = 0;
    let flatDays = 0;
    let totalMonthPnl = 0;

    calendarData.forEach((day) => {
      if (day.isCurrentMonth && day.pnl !== null) {
        totalMonthPnl += day.pnl;
        if (day.pnl > 0) winningDays++;
        else if (day.pnl < 0) losingDays++;
        else flatDays++;
      }
    });

    return { winningDays, losingDays, flatDays, totalMonthPnl };
  }, [calendarData]);

  // Find max absolute P&L for intensity scaling
  const maxAbsPnl = useMemo(() => {
    let max = 0;
    calendarData.forEach((day) => {
      if (day.pnl !== null) {
        max = Math.max(max, Math.abs(day.pnl));
      }
    });
    return max || 1;
  }, [calendarData]);

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === "prev") {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDayClick = useCallback((day: DayData) => {
    if (day.trades.length > 0) {
      setSelectedDay(day);
      setModalOpen(true);
    }
  }, []);

  const monthYearLabel = formatDate(currentDate, "MMMM yyyy");

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const isEmpty = !filteredTrades.length;

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <AnalysisCard
        title="Monthly Overview"
        subtitle={monthYearLabel}
        action={
          <button
            onClick={goToToday}
            className="text-xs text-primary hover:underline"
          >
            Today
          </button>
        }
      >
        <StatGrid columns={4} gap="sm">
          <StatCard
            label="Month P&L"
            value={isEmpty ? "$0.00" : `${monthSummary.totalMonthPnl >= 0 ? "+" : "-"}$${formatCurrency(Math.abs(monthSummary.totalMonthPnl), 2)}`}
            variant={isEmpty ? "default" : (monthSummary.totalMonthPnl >= 0 ? "positive" : "negative")}
          />
          <StatCard
            label="Winning Days"
            value={isEmpty ? 0 : monthSummary.winningDays}
            variant={isEmpty ? "default" : "positive"}
          />
          <StatCard
            label="Losing Days"
            value={isEmpty ? 0 : monthSummary.losingDays}
            variant={isEmpty ? "default" : "negative"}
          />
          <StatCard
            label="Win Rate"
            value={isEmpty ? "0%" : `${monthSummary.winningDays + monthSummary.losingDays > 0 ? ((monthSummary.winningDays / (monthSummary.winningDays + monthSummary.losingDays)) * 100).toFixed(0) : 0}%`}
            variant="default"
          />
        </StatGrid>
      </AnalysisCard>

      {/* Calendar */}
      <AnalysisCard
        title="P&L Calendar"
        subtitle="Daily profit and loss breakdown"
        action={
          <div className="flex items-center gap-2 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox
                checked={includeFees}
                onCheckedChange={(checked) => setIncludeFees(checked as boolean)}
                className="size-3.5"
              />
              <span className="text-muted-foreground">Include Fees</span>
            </label>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          {/* Month Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigateMonth("prev")}
              className="p-1.5 rounded-md hover:bg-muted transition-colors cursor-pointer"
              type="button"
            >
              <ChevronLeftIcon className="size-4 text-muted-foreground" />
            </button>
            <span className="text-sm font-semibold">{monthYearLabel}</span>
            <button
              onClick={() => navigateMonth("next")}
              className="p-1.5 rounded-md hover:bg-muted transition-colors cursor-pointer"
              type="button"
            >
              <ChevronRightIcon className="size-4 text-muted-foreground" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1.5">
            {/* Week day headers */}
            {weekDays.map((day) => (
              <div
                key={day}
                className="text-center text-sm font-semibold text-muted-foreground py-2"
              >
                {day}
              </div>
            ))}

            {/* Calendar cells */}
            {calendarData.map((day, index) => {
              const intensity =
                day.pnl !== null
                  ? Math.min(Math.abs(day.pnl) / maxAbsPnl, 1)
                  : 0;
              const isProfit = day.pnl !== null && day.pnl > 0;
              const isLoss = day.pnl !== null && day.pnl < 0;
              const baseOpacity = 0.15;
              const maxOpacity = 0.6;
              const opacity = baseOpacity + intensity * (maxOpacity - baseOpacity);
              const hasTrades = day.trades.length > 0;

              const winningTrades = day.trades.filter(
                (t) => t.netPnl > 0
              ).length;
              const losingTrades = day.trades.filter((t) => t.netPnl < 0).length;

              const cellContent = (
                <div
                  className={cn(
                    "relative rounded-lg py-2 px-1 flex flex-col items-center justify-center transition-all duration-200 group min-h-[70px]",
                    !day.isCurrentMonth && "opacity-30",
                    hasTrades && "cursor-pointer hover:scale-105"
                  )}
                  style={{
                    backgroundColor: isProfit
                      ? `rgba(46, 189, 133, ${opacity})`
                      : isLoss
                        ? `rgba(246, 70, 93, ${opacity})`
                        : undefined,
                    ["--glow-color" as string]: isProfit
                      ? `${SITE_GREEN}40`
                      : isLoss
                        ? `${SITE_RED}40`
                        : "transparent",
                  }}
                  onClick={() => handleDayClick(day)}
                >
                  {/* Day number */}
                  <span
                    className={cn(
                      "text-lg font-bold",
                      day.isCurrentMonth
                        ? "text-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    {day.dayNumber}
                  </span>

                  {/* P&L value */}
                  {day.pnl !== null && (
                    <span
                      className={cn(
                        "text-base font-bold truncate max-w-full",
                        isProfit
                          ? "text-bid-foreground"
                          : isLoss
                            ? "text-ask-foreground"
                            : "text-muted-foreground"
                      )}
                    >
                      {day.pnl >= 0 ? "+" : ""}$
                      {formatCurrency(Math.abs(day.pnl), 0)}
                    </span>
                  )}

                  {/* Glow effect on hover for days with P&L */}
                  {day.pnl !== null && (
                    <div
                      className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                      style={{
                        boxShadow: `0 0 16px ${isProfit ? SITE_GREEN : SITE_RED}60`,
                      }}
                    />
                  )}
                </div>
              );

              // If no trades, just render the cell without popover
              if (!hasTrades) {
                return <div key={index}>{cellContent}</div>;
              }

              // Render with hover popover for days with trades
              return (
                <Popover
                  key={index}
                  open={hoveredDay === day.dateStr}
                  onOpenChange={(open) => {
                    if (!open) setHoveredDay(null);
                  }}
                >
                  <PopoverTrigger asChild>
                    <div
                      onMouseEnter={() => setHoveredDay(day.dateStr)}
                      onMouseLeave={() => setHoveredDay(null)}
                    >
                      {cellContent}
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-64 p-3"
                    side="top"
                    align="center"
                    onMouseEnter={() => setHoveredDay(day.dateStr)}
                    onMouseLeave={() => setHoveredDay(null)}
                  >
                    <div className="space-y-3">
                      {/* Date Header */}
                      <div className="font-semibold text-sm border-b pb-2">
                        {formatDate(day.date, "EEE, MMM d")}
                      </div>

                      {/* Quick Stats */}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-1.5">
                          <TrendingUpIcon className="size-3 text-bid-foreground" />
                          <span className="text-muted-foreground">Wins:</span>
                          <span className="font-bold text-bid-foreground">
                            {winningTrades}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <TrendingDownIcon className="size-3 text-ask-foreground" />
                          <span className="text-muted-foreground">Losses:</span>
                          <span className="font-bold text-ask-foreground">
                            {losingTrades}
                          </span>
                        </div>
                      </div>

                      {/* P&L Breakdown */}
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Gross P&L:
                          </span>
                          <span
                            className={cn(
                              "font-bold",
                              day.grossPnl >= 0
                                ? "text-bid-foreground"
                                : "text-ask-foreground"
                            )}
                          >
                            {day.grossPnl >= 0 ? "+" : ""}$
                            {formatCurrency(Math.abs(day.grossPnl), 2)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Fees:</span>
                          <span className="text-ask-foreground">
                            -${formatCurrency(day.fees, 2)}
                          </span>
                        </div>
                        <div className="flex justify-between pt-1 border-t">
                          <span className="text-muted-foreground">Net P&L:</span>
                          <span
                            className={cn(
                              "font-bold",
                              day.grossPnl - day.fees >= 0
                                ? "text-bid-foreground"
                                : "text-ask-foreground"
                            )}
                          >
                            {day.grossPnl - day.fees >= 0 ? "+" : ""}$
                            {formatCurrency(Math.abs(day.grossPnl - day.fees), 2)}
                          </span>
                        </div>
                      </div>

                      {/* Click hint */}
                      <div className="flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground pt-2 border-t">
                        <MousePointerClickIcon className="size-3" />
                        <span>Click for details</span>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 pt-2 border-t border-border/50">
            <div className="flex items-center gap-1.5 text-[10px]">
              <div
                className="size-3 rounded"
                style={{ backgroundColor: `rgba(46, 189, 133, 0.4)` }}
              />
              <span className="text-muted-foreground">Profit</span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px]">
              <div
                className="size-3 rounded"
                style={{ backgroundColor: `rgba(246, 70, 93, 0.4)` }}
              />
              <span className="text-muted-foreground">Loss</span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px]">
              <CalendarIcon className="size-3 text-muted-foreground" />
              <span className="text-muted-foreground">No trades</span>
            </div>
          </div>
        </div>
      </AnalysisCard>

      {/* Day Trades Modal */}
      {selectedDay && (
        <DayTradesModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          day={selectedDay}
          formatDate={formatDate}
          formatTradingTime={formatTradingTime}
        />
      )}
    </div>
  );
}

/**
 * Day Trades Modal
 * Shows detailed trades for a specific day
 */
interface DayTradesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  day: DayData;
  formatDate: (date: Date | string | number, format?: string) => string;
  formatTradingTime: (date: Date | string | number) => string;
}

function DayTradesModal({ open, onOpenChange, day, formatDate, formatTradingTime }: DayTradesModalProps) {
  const formattedDate = formatDate(day.date, "EEEE, MMMM d, yyyy");

  const wins = day.trades.filter((t) => t.netPnl > 0);
  const losses = day.trades.filter((t) => t.netPnl < 0);
  const isProfit = day.grossPnl >= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <DialogHeader className="pb-4 border-b border-border/50">
          <DialogTitle className="text-base font-medium text-foreground/90">
            {formattedDate}
          </DialogTitle>
          {/* P&L + Stats inline */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-baseline gap-2">
              <span
                className={cn(
                  "text-2xl font-semibold tracking-tight",
                  isProfit ? "text-bid-foreground" : "text-ask-foreground"
                )}
              >
                {day.grossPnl >= 0 ? "+" : "-"}${formatCurrency(Math.abs(day.grossPnl), 2)}
              </span>
              <span className="text-xs text-muted-foreground">
                before ${formatCurrency(day.fees, 2)} fees
              </span>
            </div>
            {/* Inline stats */}
            <div className="flex items-center gap-6 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground">Won:</span>
                <span className="font-medium text-bid-foreground">{wins.length}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground">Lost:</span>
                <span className="font-medium text-ask-foreground">{losses.length}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground">Total:</span>
                <span className="font-medium">{day.trades.length}</span>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Trades List */}
        <div className="flex-1 overflow-y-auto py-4 space-y-3 min-h-0">
          {day.trades.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No trades on this day
            </div>
          ) : (
            day.trades
              .sort(
                (a, b) => b.exitTime.getTime() - a.exitTime.getTime()
              )
              .map((trade) => {
                const pnl = trade.netPnl;
                const isWin = pnl > 0;
                const isLoss = pnl < 0;
                const time = formatTradingTime(trade.exitTime);
                const duration = formatDuration(trade.durationMs);

                return (
                  <div
                    key={trade.id}
                    className={cn(
                      "p-4 rounded-lg border",
                      isWin
                        ? "border-bid-foreground/30"
                        : isLoss
                          ? "border-ask-foreground/30"
                          : "border-border"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{trade.market}</span>
                        <span
                          className={cn(
                            "text-xs font-medium",
                            trade.side === "long"
                              ? "text-bid-foreground"
                              : "text-ask-foreground"
                          )}
                        >
                          {trade.side === "long" ? "Long" : "Short"}
                        </span>
                      </div>
                      <span
                        className={cn(
                          "font-semibold text-base",
                          isWin
                            ? "text-bid-foreground"
                            : isLoss
                              ? "text-ask-foreground"
                              : "text-muted-foreground"
                        )}
                      >
                        {pnl >= 0 ? "+" : "-"}$
                        {formatCurrency(Math.abs(pnl), 2)}
                      </span>
                    </div>
                    {/* Row 1: Entry, Exit, Value */}
                    <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
                      <span>Entry:</span>
                      <span className="text-foreground/80">
                        ${formatCurrency(trade.entryPrice, 2)}
                      </span>
                      <span>→</span>
                      <span>Exit:</span>
                      <span className="text-foreground/80">
                        ${formatCurrency(trade.exitPrice, 2)}
                      </span>
                      <span className="text-muted-foreground/50">|</span>
                      <span>Value:</span>
                      <span className="text-foreground/80">${formatCurrency(trade.volume, 0)}</span>
                    </div>
                    {/* Row 2: Duration, Time */}
                    <div className="flex items-center justify-between mt-1.5 text-xs text-muted-foreground">
                      <span>
                        <span>Duration: </span>
                        <span className="text-foreground/80">{duration}</span>
                      </span>
                      <span className="text-foreground/50">{time}</span>
                    </div>
                  </div>
                );
              })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatDuration(ms: number): string {
  if (ms < 60 * 1000) return `${Math.round(ms / 1000)}s`;
  if (ms < 60 * 60 * 1000) return `${Math.round(ms / (60 * 1000))}m`;
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const mins = Math.round((ms % (60 * 60 * 1000)) / (60 * 1000));
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

